/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/restrict-template-expressions */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import AWS from 'aws-sdk';
import config from '../config.json';
import { clearStore } from '../app/store';
import { unsubscribePushService } from '../serviceWorker';
import { clearCaches } from '../api';
const MFA_DISABLED = process.env.REACT_APP_MFA_DISABLED === 'true';

export interface ICognitoDevice {
  DeviceAttributes: { Name: string, Value: string }[];
  DeviceCreateDate: number;
  DeviceKey: string;
  DeviceLastAuthenticatedDate: number;
  DeviceLastModifiedDate: number;
}

export enum ETokenType {
  ID, ACCESS
}

export enum EMfaStatus {
  SETUP = 'setup',
  MISSING_PHONE = 'missing-phone',
  PHONE_NEEDS_VERIFICATION = 'phone-needs-verification',
  DISABLED = 'disabled',
}

enum EUserAttributes {
  PHONE_NUMBER = 'phone_number',
  PHONE_NUMBER_VERIFIED = 'phone_number_verified',
  ACCEPTS_DATA_POLICY = 'custom:accepts_data_policy',
  ACCEPTS_DISCLAIMER = 'custom:accepts_disclaimer',
}

export abstract class AuthChallenge {
  cognitoUser: CognitoUser;

  constructor(cognitoUser: CognitoUser) {
    this.cognitoUser = cognitoUser;
  }
}

export class NewPasswordChallenge extends AuthChallenge {
  sessionUserAttributes: any;

  constructor(cognitoUser: CognitoUser, sessionUserAttributes: any) {
    super(cognitoUser);

    this.sessionUserAttributes = sessionUserAttributes;
  }
}

export class MfaSmsChallenge extends AuthChallenge {
  smsDestination: string;

  constructor(cognitoUser: CognitoUser, smsDestination: string) {
    super(cognitoUser);

    this.smsDestination = smsDestination;
  }
}

export class MfaSetupPhoneChallenge extends AuthChallenge {
}

export class MfaVerifyPhoneChallenge extends AuthChallenge {
}

export class AcceptPoliciesChallenge extends AuthChallenge {
  readonly session: CognitoUserSession;

  constructor(cognitoUser: CognitoUser, session: CognitoUserSession) {
    super(cognitoUser);

    this.session = session;
  }
}

export class AuthToken {
  jwtToken: string;
  constructor(jwtToken: string) {
    this.jwtToken = jwtToken;
  }
}

export class NotAuthorizedError extends Error {
  constructor() {
    super('There is no user authorized on this machine.');
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = NotAuthorizedError.name;
  }
}

export class SessionInvalidError extends Error {
  constructor() {
    super('Session is nolonger valid.');
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = SessionInvalidError.name;
  }
}

export class AuthHelper {
  // Info https://www.npmjs.com/package/amazon-cognito-identity-js
  private static readonly poolData = {
    UserPoolId: config.awsUserPoolId,
    ClientId: config.awsClientId,
  };

  private static readonly userPool = new CognitoUserPool(AuthHelper.poolData);
  private static readonly identityPoolId = config.awsIdentityPoolId;
  private static readonly region = config.awsRegion;

  private static getCognitoUser(username: string): CognitoUser {
    const userData = { Username: username, Pool: AuthHelper.userPool };
    return new CognitoUser(userData);
  }

  private static resolveUserMfaStatusBasedOnAttributes(cognitoUser: CognitoUser): Promise<EMfaStatus> { // phone_number, phone_number_verified
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        if (!attributes) {
          resolve(EMfaStatus.MISSING_PHONE);
          return;
        }

        const hasPhone = attributes.some(attribute => attribute.Name === EUserAttributes.PHONE_NUMBER && attribute.Value);
        if (!hasPhone) {
          resolve(EMfaStatus.MISSING_PHONE);
          return;
        }

        const phoneIsVerified = attributes.some(attribute => attribute.Name === EUserAttributes.PHONE_NUMBER_VERIFIED && attribute.Value === 'true');
        if (!phoneIsVerified) {
          resolve(EMfaStatus.PHONE_NEEDS_VERIFICATION);
          return;
        }

        resolve(EMfaStatus.DISABLED);
      });
    });
  }

  private static getUserMfaStatus(cognitoUser: CognitoUser): Promise<EMfaStatus> {
    return new Promise((resolve, reject) => {
      cognitoUser.getMFAOptions((err, options) => {
        if (err) {
          reject(err);
          return;
        }

        if (options === undefined) {
          // Missing/disabled
          resolve(AuthHelper.resolveUserMfaStatusBasedOnAttributes(cognitoUser));
        } else {
          resolve(EMfaStatus.SETUP);
        }
      });
    });
  }

  private static getUserPolicyStatus(cognitoUser: CognitoUser): Promise<{ acceptedDataPolicy: boolean, acceptedDisclaimer: boolean }> {
    // custom:accepts_disclaimer, custom:accepts_data_policy
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        if (!attributes) {
          resolve({
            acceptedDataPolicy: false,
            acceptedDisclaimer: false,
          });
          return;
        }

        const acceptedDataPolicy = attributes.some(attribute => attribute.Name === EUserAttributes.ACCEPTS_DATA_POLICY && attribute.Value === 'true');
        const acceptedDisclaimer = attributes.some(attribute => attribute.Name === EUserAttributes.ACCEPTS_DISCLAIMER && attribute.Value === 'true');

        resolve({
          acceptedDataPolicy,
          acceptedDisclaimer,
        });
      });
    });
  }

  private static async getUserTokenOrChallenge(cognitoUser: CognitoUser, session: CognitoUserSession): Promise<AuthToken|AuthChallenge> {
    const [policyStatus, mfaStatus] = await Promise.all([AuthHelper.getUserPolicyStatus(cognitoUser), AuthHelper.getUserMfaStatus(cognitoUser)]);

    if (!policyStatus.acceptedDataPolicy || !policyStatus.acceptedDisclaimer) {
      return new AcceptPoliciesChallenge(cognitoUser, session);
    }
    switch (mfaStatus) {
      case EMfaStatus.DISABLED:
        if (!MFA_DISABLED) {
          await AuthHelper.enableUserMfa(cognitoUser);
        }
        return AuthHelper.processUserSession(session);
      case EMfaStatus.SETUP:
        return AuthHelper.processUserSession(session);
      case EMfaStatus.MISSING_PHONE:
        if (MFA_DISABLED) {
          return AuthHelper.processUserSession(session);
        }
        return new MfaSetupPhoneChallenge(cognitoUser);
      case EMfaStatus.PHONE_NEEDS_VERIFICATION:
        if (MFA_DISABLED) {
          return AuthHelper.processUserSession(session);
        }
        return AuthHelper.getPhoneVerificationChallenge(cognitoUser);
      default:
        console.error('Unknown mfaStatus', mfaStatus);
        throw new Error('Unknown MFA status');
    }
  }

  public static login(username: string, password: string): Promise<AuthToken|AuthChallenge> {
    clearCaches();
    const authenticationData = { Username: username, Password: password };
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = AuthHelper.getCognitoUser(username);
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(AuthHelper.getUserTokenOrChallenge(cognitoUser, session));
        },
        onFailure: (err: any) => {
          reject(err);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
          delete userAttributes.email_verified; // the api doesn't accept this field back
          resolve(new NewPasswordChallenge(cognitoUser, userAttributes));
        },
        mfaRequired: (challengeName, challengeParameters) => {
          if (typeof challengeParameters !== 'object' || challengeName !== 'SMS_MFA') {
            console.error('Unexpected mfa response', { challengeName, challengeParameters });
            throw new Error('Unknown mfa challenge');
          }

          const parameters = challengeParameters as { CODE_DELIVERY_DELIVERY_MEDIUM?: string, CODE_DELIVERY_DESTINATION?: string };
          resolve(new MfaSmsChallenge(cognitoUser, parameters.CODE_DELIVERY_DESTINATION || ''));
        },
      });
    });
  }

  public static enableUserMfa(cognitoUser: CognitoUser): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser.enableMFA((err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }

  public static handleMfaVerifyPhoneChallenge(code: string, mfaVerifyPhoneChallenge: MfaVerifyPhoneChallenge): Promise<void> {
    const { cognitoUser } = mfaVerifyPhoneChallenge;
    return new Promise((resolve, reject) => {
      cognitoUser.verifyAttribute(EUserAttributes.PHONE_NUMBER, code, {
        onSuccess: (result) => {
          if (result !== 'SUCCESS') {
            reject(new Error('Unexpected response from cognito'));
            return;
          }

          resolve(AuthHelper.enableUserMfa(cognitoUser));
        },
        onFailure: reject,
      });
    });
  }

  public static trustDevice(cognitoUser: CognitoUser): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser.setDeviceStatusRemembered({
        onSuccess: (result) => {
          if (result !== 'SUCCESS') {
            reject(new Error('Unexpected response from cognito'));
            return;
          }
          resolve();
        },
        onFailure: reject,
      });
    });
  }

  public static listDevices(): Promise<ICognitoDevice[]> {
    const cognitoUser = AuthHelper.getCurrentUser();
    return new Promise((resolve, reject) => {
      cognitoUser.listDevices(0, null, {
        onSuccess: (data) => {
          resolve((data?.Devices || []) as ICognitoDevice[]);
        },
        onFailure: reject,
      });
    });
  }

  public static forgetDevice(deviceKey: string): Promise<void> {
    const cognitoUser = AuthHelper.getCurrentUser();
    return new Promise((resolve, reject) => {
      cognitoUser.forgetSpecificDevice(deviceKey, {
        onSuccess: (result) => {
          if (result !== 'SUCCESS') {
            reject(new Error('Unexpected response from cognito'));
            return;
          }
          resolve();
        },
        onFailure: reject,
      });
    });
  }

  public static handleAcceptPoliciesChallenge(acceptPoliciesChallenge: AcceptPoliciesChallenge): Promise<AuthChallenge | AuthToken> {
    const { cognitoUser, session } = acceptPoliciesChallenge;
    return new Promise((resolve, reject) => {
      cognitoUser.updateAttributes([
        { Name: EUserAttributes.ACCEPTS_DATA_POLICY, Value: 'true' },
        { Name: EUserAttributes.ACCEPTS_DISCLAIMER, Value: 'true' },
      ], (err, result) => {
        if (err) {
          reject(err);
          return;
        }

        if (result !== 'SUCCESS') {
          reject(new Error('Unexpected response from cognito'));
          return;
        }

        resolve(AuthHelper.getUserTokenOrChallenge(cognitoUser, session));
      });
    });
  }

  private static getPhoneVerificationChallenge(cognitoUser: CognitoUser): Promise<MfaVerifyPhoneChallenge> {
    return new Promise((resolve, reject) => {
      cognitoUser.getAttributeVerificationCode(EUserAttributes.PHONE_NUMBER, {
        onSuccess: (result) => {
          if (result !== 'SUCCESS') {
            reject(new Error('Unexpected response from cognito'));
            return;
          }

          resolve(new MfaVerifyPhoneChallenge(cognitoUser));
        },
        onFailure: reject,
      });
    });
  }

  public static handleMfaSetupPhoneChallenge(
    phoneNumber: string,
    mfaSmsChallenge: MfaSetupPhoneChallenge,
  ): Promise<MfaVerifyPhoneChallenge> {
    return new Promise((resolve, reject) => {
      const { cognitoUser } = mfaSmsChallenge;
      const cleanPhoneNumber = phoneNumber.trim().replaceAll(/[^0-9+]/g, '');
      cognitoUser.updateAttributes([{ Name: EUserAttributes.PHONE_NUMBER, Value: cleanPhoneNumber }], (err, result) => {
        if (err) {
          reject(err);
          return;
        }

        if (result !== 'SUCCESS') {
          reject(new Error('Unexpected response from cognito'));
          return;
        }

        resolve(AuthHelper.getPhoneVerificationChallenge(mfaSmsChallenge.cognitoUser));
      });
    });
  }

  public static handleMfaSmsChallenge(
    code: string,
    mfaSmsChallenge: MfaSmsChallenge
  ): Promise<AuthToken> {
    return new Promise((resolve, reject) => {
      const { cognitoUser } = mfaSmsChallenge;
      cognitoUser.sendMFACode(code, {
        onSuccess: (session: CognitoUserSession) => resolve(AuthHelper.processUserSession(session)),
        onFailure: (err: any) => {
          reject(err);
        }
      });
    });
  }

  public static handleNewPasswordChallenge(
    newPassword: string,
    newPasswordChallenge: NewPasswordChallenge
  ): Promise<AuthToken|AuthChallenge> {
    return new Promise((resolve, reject) => {
      const { cognitoUser, sessionUserAttributes } = newPasswordChallenge;
      cognitoUser.completeNewPasswordChallenge(newPassword, { email: sessionUserAttributes.email }, {
        onSuccess: (session: CognitoUserSession) => resolve(AuthHelper.getUserTokenOrChallenge(cognitoUser, session)),
        onFailure: (err: any) => {
          reject(err);
        },
        mfaRequired: (challengeName, challengeParameters) => {
          if (typeof challengeParameters !== 'object' || challengeName !== 'SMS_MFA') {
            console.error('Unexpected mfa response', { challengeName, challengeParameters });
            throw new Error('Unknown mfa challenge');
          }

          const parameters = challengeParameters as { CODE_DELIVERY_DELIVERY_MEDIUM?: string, CODE_DELIVERY_DESTINATION?: string };
          resolve(new MfaSmsChallenge(cognitoUser, parameters.CODE_DELIVERY_DESTINATION || ''));
        },
      });
    });
  }

  public static changePassword(currentPassword: string, newPassword: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      const cognitoUser = AuthHelper.getCurrentUser();
      cognitoUser.changePassword(currentPassword, newPassword, (err?: Error) => {
        if (err) {
          reject(err);
        } else {
          resolve(undefined);
        }
      });
    });
  }

  public static requestPasswordForgot(username: string): Promise<void> {
    const cognitoUser = AuthHelper.getCognitoUser(username);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: resolve,
        onFailure: reject,
      });
    });
  }

  public static passwordForgotChange(code: string, username: string, newPassword: string): Promise<void> {
    const cognitoUser = AuthHelper.getCognitoUser(username);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: reject,
      });
    });
  }

  private static processUserSession(session: CognitoUserSession): AuthToken {
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: AuthHelper.identityPoolId,
      Logins: {
        [`cognito-idp.${AuthHelper.region}.amazonaws.com/${AuthHelper.poolData.UserPoolId}`]: session
          .getIdToken()
          .getJwtToken(),
      },
    });

    return new AuthToken(session.getIdToken().getJwtToken());
  }

  private static getCognitoSession(): Promise<CognitoUserSession|null> {
    const cognitoUser = AuthHelper.getCurrentUser();
    return new Promise((resolve) => {
      cognitoUser.getSession((error: Error|null, session: CognitoUserSession|null) => {
        if (error) {
          console.warn('Could not get session', error);
          resolve(null);
          return;
        }

        resolve(session);
      });
    });
  }

  private static async getToken(type: ETokenType): Promise<string> {
    const session = await AuthHelper.getCognitoSession();
    if (session) {
      switch (type) {
        case ETokenType.ACCESS:
          return session.getAccessToken().getJwtToken();
        case ETokenType.ID:
          return session.getIdToken().getJwtToken();
        default:
          throw new Error('Unexpected ETokenType');
      }
    }
    throw new NotAuthorizedError();
  }

  public static getAccessJwtToken(): Promise<string> {
    return AuthHelper.getToken(ETokenType.ACCESS);
  }

  public static getIdJwtToken(): Promise<string> {
    return AuthHelper.getToken(ETokenType.ID);
  }

  public static async isAuthenticated(): Promise<boolean> {
    try {
      const token = await AuthHelper.getAccessJwtToken();
      return !!token;
    } catch (err) {
      return false;
    }
  }

  public static getCurrentUser(): CognitoUser {
    const cognitoUser = AuthHelper.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err: Error|null, session: CognitoUserSession|null) => {
        if (err) {
          console.error('Getting session failed', err.message, err);
          throw new NotAuthorizedError();
        }

        if (session && session.isValid()) {
          AuthHelper.processUserSession(session);
        } else {
          throw new SessionInvalidError();
        }
      });
      return cognitoUser;
    } else {
      throw new NotAuthorizedError();
    }
  }

  public static signOut(): void {
    try {
      clearStore();
      clearCaches();
      const cognitoUser = AuthHelper.getCurrentUser();
      cognitoUser.signOut();
      unsubscribePushService().catch(err => console.error('Failed to unsubscribe from push service', err));
    } catch (err) {
      console.warn('Logout failed, probably no session active.', err);
    }
  }
}
